import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { loadLocale } from "wuchale/load-utils";
import { type Locale, SUPPORTED_LOCALES } from "@/lib/locales";
import { setLocale as setLoaderLocale } from "@/locales/main.loader.js";

// Side-effect: registers loaders so loadLocale() works
import "@/locales/main.loader";

export type { Locale };

const DEFAULT_LOCALE: Locale = "en";
const STORAGE_KEY = "app-language";

function isValidLocale(value: string | null): value is Locale {
	return SUPPORTED_LOCALES.includes(value as Locale);
}

type I18nContextType = {
	locale: Locale;
	setLocale: (locale: Locale) => Promise<void>;
	isLoading: boolean;
};

export const I18nContext = createContext<I18nContextType | undefined>(
	undefined,
);

export function I18nProvider({ children }: { children: React.ReactNode }) {
	const [locale, setLocaleState] = useState<Locale>(() => {
		const saved = localStorage.getItem(STORAGE_KEY);
		const initial = isValidLocale(saved) ? saved : DEFAULT_LOCALE;
		setLoaderLocale(initial);
		return initial;
	});

	const [initiallyLoaded, setInitiallyLoaded] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			setIsLoading(true);
			try {
				await loadLocale(locale);
				if (cancelled) return;
				setLoaderLocale(locale);
				document.documentElement.lang = locale;
			} catch (err) {
				console.error(`Wuchale: failed to load locale ${locale}`, err);
			} finally {
				if (!cancelled) {
					setInitiallyLoaded(true);
					setIsLoading(false);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [locale]);

	const setLocale = async (newLocale: Locale) => {
		if (newLocale === locale) return;
		setLocaleState(newLocale);
		localStorage.setItem(STORAGE_KEY, newLocale);
	};

	return (
		<I18nContext.Provider value={{ locale, setLocale, isLoading }}>
			{initiallyLoaded ? children : null}
		</I18nContext.Provider>
	);
}

export function useI18n() {
	const context = useContext(I18nContext);
	if (!context) {
		throw new Error("useI18n must be used within I18nProvider");
	}
	return context;
}
